import { createApi } from "@reduxjs/toolkit/query/react";

import { type IApiResponse } from "@/shared/interfaces/api-response.interface";
import type { IEnqueueDistrictImportResponse } from "@/shared/interfaces/district-import.interface";

import { ApiTags } from "../api-tags.enum";
import customFetchBase from "../custom-fetch-base";
import districtsApi from "../districts/districts";
import reportsApi, {
  NEW_HOUSING_PROJECTS_REPORT_TAG,
} from "../reports/reports";

import { applyProjectDetailsToListItem } from "./apply-project-details-to-list-item";

import type {
  ICreateProjectUploadUrlRequest,
  IProjectUploadUrlResponse,
  IImportNewHousingRequest,
  IImportNewHousingResponse,
  IFinalizeNewHousingDataImportRequest,
  IImportProjectBoundariesRequest,
  IImportProjectBoundariesResponse,
  ICreateNewHousingGeocodingJobRequest,
  ICreateNewHousingGeocodingJobResponse,
  ICreateProjectStudentsJobRequest,
  ICreateProjectStudentsJobResponse,
  IProjectWithDetailsResponse,
  IListNewHousingProjectsParams,
  INewHousingProjectListResponse,
  ICreateNewHousingProjectRequest,
  IUpdateNewHousingProjectRequest,
  IReplaceProjectOccupancyScheduleRequest,
  IUpdateProjectGenerationRatesRequest,
  IProjectGenerationRatesResponse,
  IGetDistrictDefaultGenerationRatesParams,
  IDistrictDefaultGenerationRatesResponse,
  IUpsertDistrictDefaultGenerationRatesRequest,
  IProjectSchoolAllocationsResponse,
  IReplaceProjectSchoolAllocationsRequest,
  IDistrictAffectedSchoolsDataResponse,
  INewHousingReviewCountsResponse,
  IHousingSourceSyncStatusResponse,
  ISyncNewHousingIngestRequest,
  IResolveHousingCoverageResponse,
} from "./projects.interface";

const projectsApi = createApi({
  reducerPath: "projectsApi",
  baseQuery: customFetchBase,
  tagTypes: [ApiTags.Documents, ApiTags.Projects],
  // Keep list/detail fresh after same-tab remount and after Full Details edits in another tab.
  refetchOnFocus: true,
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    listNewHousingProjects: builder.query<
      INewHousingProjectListResponse,
      IListNewHousingProjectsParams
    >({
      query: (params) => ({
        url: "/projects",
        method: "GET",
        params,
      }),
      providesTags: (result) =>
        result
          ? [
            ...result.items.map(({ id }) => ({
              type: ApiTags.Projects as const,
              id,
            })),
            { type: ApiTags.Projects, id: "LIST" },
          ]
          : [{ type: ApiTags.Projects, id: "LIST" }],
      transformResponse: (
        result: IApiResponse<INewHousingProjectListResponse>,
      ) => result.data,
    }),
    createNewHousingProject: builder.mutation<
      IProjectWithDetailsResponse,
      ICreateNewHousingProjectRequest
    >({
      query: (body) => ({
        url: "/projects",
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: ApiTags.Projects, id: "LIST" },
        { type: ApiTags.Projects, id: `${arg.districtId}-affected-schools` },
        ApiTags.Documents,
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            reportsApi.util.invalidateTags([NEW_HOUSING_PROJECTS_REPORT_TAG]),
          );
          dispatch(districtsApi.util.invalidateTags([ApiTags.Districts]));
        } catch {
          // Leave report / district cache intact when the mutation fails.
        }
      },
      transformResponse: (result: IApiResponse<IProjectWithDetailsResponse>) =>
        result.data,
    }),
    updateNewHousingProject: builder.mutation<
      IProjectWithDetailsResponse,
      {
        projectId: string;
        districtId?: string;
        body: IUpdateNewHousingProjectRequest;
      }
    >({
      query: ({ projectId, body }) => ({
        url: `/projects/${projectId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { projectId, districtId }) => {
        const tags: { type: ApiTags.Projects; id: string }[] = [
          { type: ApiTags.Projects, id: projectId },
          { type: ApiTags.Projects, id: "LIST" },
        ];
        if (districtId) {
          tags.push({
            type: ApiTags.Projects,
            id: `${districtId}-affected-schools`,
          });
        }

        return tags;
      },
      async onQueryStarted({ projectId }, lifecycleApi) {
        const { dispatch, queryFulfilled } = lifecycleApi;
        try {
          const { data } = await queryFulfilled;
          syncProjectCachesFromDetails(dispatch, () => lifecycleApi.getState(), projectId, data);
          dispatch(
            reportsApi.util.invalidateTags([NEW_HOUSING_PROJECTS_REPORT_TAG]),
          );
          dispatch(districtsApi.util.invalidateTags([ApiTags.Districts]));
        } catch {
          // Leave report / district cache intact when the mutation fails.
        }
      },
      transformResponse: (result: IApiResponse<IProjectWithDetailsResponse>) =>
        result.data,
    }),
    deleteNewHousingProject: builder.mutation<
      void,
      { projectId: string; districtId: string }
    >({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { projectId, districtId }) => [
        { type: ApiTags.Projects, id: projectId },
        { type: ApiTags.Projects, id: "LIST" },
        { type: ApiTags.Projects, id: `${districtId}-affected-schools` },
        ApiTags.Documents,
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            reportsApi.util.invalidateTags([NEW_HOUSING_PROJECTS_REPORT_TAG]),
          );
          dispatch(districtsApi.util.invalidateTags([ApiTags.Districts]));
        } catch {
          // Leave report / district cache intact when the mutation fails.
        }
      },
    }),
    replaceProjectOccupancySchedule: builder.mutation<
      IProjectWithDetailsResponse,
      {
        projectId: string;
        districtId?: string;
        body: IReplaceProjectOccupancyScheduleRequest;
      }
    >({
      query: ({ projectId, body }) => ({
        url: `/projects/${projectId}/occupancy-schedule`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { projectId, districtId }) => {
        const tags: { type: ApiTags.Projects; id: string }[] = [
          { type: ApiTags.Projects, id: projectId },
          { type: ApiTags.Projects, id: "LIST" },
        ];
        if (districtId) {
          tags.push({
            type: ApiTags.Projects,
            id: `${districtId}-affected-schools`,
          });
        }

        return tags;
      },
      async onQueryStarted({ projectId }, lifecycleApi) {
        const { dispatch, queryFulfilled } = lifecycleApi;
        try {
          const { data } = await queryFulfilled;
          syncProjectCachesFromDetails(dispatch, () => lifecycleApi.getState(), projectId, data);
          dispatch(
            reportsApi.util.invalidateTags([NEW_HOUSING_PROJECTS_REPORT_TAG]),
          );
        } catch {
          // Leave report cache intact when the mutation fails.
        }
      },
      transformResponse: (result: IApiResponse<IProjectWithDetailsResponse>) =>
        result.data,
    }),
    getProjectGenerationRates: builder.query<
      IProjectGenerationRatesResponse,
      string
    >({
      query: (projectId) => ({
        url: `/projects/${projectId}/generation-rates`,
        method: "GET",
      }),
      providesTags: (_r, _e, projectId) => [
        { type: ApiTags.Projects, id: projectId },
      ],
      transformResponse: (
        result: IApiResponse<IProjectGenerationRatesResponse>,
      ) => result.data,
    }),
    updateProjectGenerationRates: builder.mutation<
      IProjectGenerationRatesResponse,
      {
        projectId: string;
        districtId?: string;
        body: IUpdateProjectGenerationRatesRequest;
      }
    >({
      query: ({ projectId, body }) => ({
        url: `/projects/${projectId}/generation-rates`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { projectId, districtId }) => {
        const tags: { type: ApiTags.Projects; id: string }[] = [
          { type: ApiTags.Projects, id: projectId },
        ];
        if (districtId) {
          tags.push({
            type: ApiTags.Projects,
            id: `${districtId}-affected-schools`,
          });
        }

        return tags;
      },
      transformResponse: (
        result: IApiResponse<IProjectGenerationRatesResponse>,
      ) => result.data,
    }),
    getDistrictDefaultGenerationRates: builder.query<
      IDistrictDefaultGenerationRatesResponse,
      IGetDistrictDefaultGenerationRatesParams
    >({
      query: (params) => ({
        url: "/projects/default-generation-rates",
        method: "GET",
        params,
      }),
      providesTags: (_r, _e, arg) => [
        { type: ApiTags.Projects, id: `defaults-${arg.districtId}` },
      ],
      transformResponse: (
        result: IApiResponse<IDistrictDefaultGenerationRatesResponse>,
      ) => result.data,
    }),
    upsertDistrictDefaultGenerationRates: builder.mutation<
      IDistrictDefaultGenerationRatesResponse,
      IUpsertDistrictDefaultGenerationRatesRequest
    >({
      query: (body) => ({
        url: "/projects/default-generation-rates",
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: ApiTags.Projects, id: `defaults-${arg.districtId}` },
      ],
      transformResponse: (
        result: IApiResponse<IDistrictDefaultGenerationRatesResponse>,
      ) => result.data,
    }),
    createProjectUploadUrl: builder.mutation<
      IProjectUploadUrlResponse,
      ICreateProjectUploadUrlRequest
    >({
      query: (body) => ({
        url: "/projects/import/upload-url",
        method: "POST",
        body,
      }),
      transformResponse: (result: IApiResponse<IProjectUploadUrlResponse>) =>
        result.data,
    }),
    importNewHousing: builder.mutation<
      IImportNewHousingResponse,
      IImportNewHousingRequest
    >({
      query: (body) => ({
        url: "/projects/import",
        method: "POST",
        body,
      }),
      // Validate-only: projects are created on finalize, so do not invalidate LIST here.
      invalidatesTags: [ApiTags.Documents],
      transformResponse: (result: IApiResponse<IImportNewHousingResponse>) =>
        result.data,
    }),
    importProjectBoundaries: builder.mutation<
      IImportProjectBoundariesResponse,
      IImportProjectBoundariesRequest
    >({
      query: (body) => ({
        url: "/projects/import/boundaries",
        method: "POST",
        body,
      }),
      invalidatesTags: [ApiTags.Documents],
      transformResponse: (
        result: IApiResponse<IImportProjectBoundariesResponse>,
      ) => result.data,
    }),
    createNewHousingGeocodingJob: builder.mutation<
      ICreateNewHousingGeocodingJobResponse,
      ICreateNewHousingGeocodingJobRequest
    >({
      query: (body) => ({
        url: "/projects/geocoding-job",
        method: "POST",
        body,
      }),
      transformResponse: (
        result: IApiResponse<ICreateNewHousingGeocodingJobResponse>,
      ) => result.data,
    }),
    createNewHousingReverseGeocodingJob: builder.mutation<
      ICreateNewHousingGeocodingJobResponse,
      ICreateNewHousingGeocodingJobRequest
    >({
      query: (body) => ({
        url: "/projects/reverse-geocoding-job",
        method: "POST",
        body,
      }),
      invalidatesTags: [ApiTags.Documents],
      transformResponse: (
        result: IApiResponse<ICreateNewHousingGeocodingJobResponse>,
      ) => result.data,
    }),
    createProjectStudentsJob: builder.mutation<
      ICreateProjectStudentsJobResponse,
      ICreateProjectStudentsJobRequest
    >({
      query: (body) => ({
        url: "/projects/project-students-job",
        method: "POST",
        body,
      }),
      transformResponse: (
        result: IApiResponse<ICreateProjectStudentsJobResponse>,
      ) => result.data,
    }),
    finalizeNewHousingDataImport: builder.mutation<
      IEnqueueDistrictImportResponse,
      {
        newHousingDataImportId: string;
        districtId: string;
        body: IFinalizeNewHousingDataImportRequest;
      }
    >({
      query: ({ newHousingDataImportId, body }) => ({
        url: `/projects/import/new-housing/${newHousingDataImportId}/finalize`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { districtId }) => [
        ApiTags.Documents,
        { type: ApiTags.Projects, id: "LIST" },
        { type: ApiTags.Projects, id: `${districtId}-affected-schools` },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            reportsApi.util.invalidateTags([NEW_HOUSING_PROJECTS_REPORT_TAG]),
          );
        } catch {
          // Leave report cache intact when the mutation fails.
        }
      },
      transformResponse: (
        result:
          | IApiResponse<IEnqueueDistrictImportResponse>
          | {
              statusCode: number;
              data: IEnqueueDistrictImportResponse;
              [key: string]: unknown;
            },
      ) =>
        "data" in result && result.data
          ? result.data
          : (result as unknown as IEnqueueDistrictImportResponse),
    }),
    deleteNewHousingDataImport: builder.mutation<void, string>({
      query: (newHousingDataImportId) => ({
        url: `/projects/import/new-housing/${newHousingDataImportId}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        ApiTags.Documents,
        { type: ApiTags.Projects, id: "LIST" },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            reportsApi.util.invalidateTags([NEW_HOUSING_PROJECTS_REPORT_TAG]),
          );
        } catch {
          // Leave report cache intact when the mutation fails.
        }
      },
    }),
    getProjectWithDetails: builder.query<IProjectWithDetailsResponse, string>({
      query: (projectId) => ({
        url: `/projects/${projectId}`,
        method: "GET",
      }),
      providesTags: (_r, _e, projectId) => [
        { type: ApiTags.Projects, id: projectId },
      ],
      transformResponse: (result: IApiResponse<IProjectWithDetailsResponse>) =>
        result.data,
    }),
    getProjectSchoolAllocations: builder.query<
      IProjectSchoolAllocationsResponse,
      string
    >({
      query: (projectId) => ({
        url: `/projects/${projectId}/school-allocations`,
        method: "GET",
      }),
      providesTags: (_r, _e, projectId) => [
        { type: ApiTags.Projects, id: `${projectId}-allocations` },
      ],
      transformResponse: (
        result: IApiResponse<IProjectSchoolAllocationsResponse>,
      ) => result.data,
    }),
    getDistrictAffectedSchoolsData: builder.query<
      IDistrictAffectedSchoolsDataResponse,
      { districtId: string }
    >({
      query: ({ districtId }) => ({
        url: "/projects/affected-schools-data",
        method: "GET",
        params: { districtId },
      }),
      providesTags: (_r, _e, { districtId }) => [
        { type: ApiTags.Projects, id: `${districtId}-affected-schools` },
      ],
      transformResponse: (
        result: IApiResponse<IDistrictAffectedSchoolsDataResponse>,
      ) => result.data,
    }),
    replaceProjectSchoolAllocations: builder.mutation<
      IProjectSchoolAllocationsResponse,
      {
        projectId: string;
        districtId?: string;
        body: IReplaceProjectSchoolAllocationsRequest;
      }
    >({
      query: ({ projectId, body }) => ({
        url: `/projects/${projectId}/school-allocations`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { projectId, districtId }) => {
        const tags: { type: ApiTags.Projects; id: string }[] = [
          { type: ApiTags.Projects, id: `${projectId}-allocations` },
          { type: ApiTags.Projects, id: projectId },
        ];
        if (districtId) {
          tags.push({
            type: ApiTags.Projects,
            id: `${districtId}-affected-schools`,
          });
        }

        return tags;
      },
      transformResponse: (
        result: IApiResponse<IProjectSchoolAllocationsResponse>,
      ) => result.data,
    }),
    getNewHousingReviewCounts: builder.query<
      INewHousingReviewCountsResponse,
      { districtId: string }
    >({
      query: (params) => ({
        url: "/projects/review-counts",
        method: "GET",
        params,
      }),
      providesTags: (_r, _e, { districtId }) => [
        { type: ApiTags.Projects, id: `${districtId}-review-counts` },
      ],
      transformResponse: (
        result: IApiResponse<INewHousingReviewCountsResponse>,
      ) => result.data,
    }),
    getHousingSourceSyncStatus: builder.query<
      IHousingSourceSyncStatusResponse,
      { districtId: string; source: string }
    >({
      query: (params) => ({
        url: "/projects/ingest/sync",
        method: "GET",
        params,
      }),
      providesTags: (_r, _e, { districtId, source }) => [
        { type: ApiTags.Projects, id: `${districtId}-sync-${source}` },
      ],
      transformResponse: (
        result: IApiResponse<IHousingSourceSyncStatusResponse>,
      ) => result.data,
    }),
    updateHousingAutoSync: builder.mutation<
      { autoSyncEnabled: boolean },
      ISyncNewHousingIngestRequest & { autoSyncEnabled: boolean }
    >({
      query: (body) => ({
        url: "/projects/ingest/auto-sync",
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { districtId, source }) => [
        { type: ApiTags.Projects, id: `${districtId}-sync-${source}` },
      ],
      transformResponse: (
        result: IApiResponse<{ autoSyncEnabled: boolean }>,
      ) => result.data,
    }),
    resolveHousingCoverage: builder.mutation<
      IResolveHousingCoverageResponse,
      ISyncNewHousingIngestRequest
    >({
      query: (body) => ({
        url: "/projects/ingest/coverage/resolve",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { districtId, source }) => [
        { type: ApiTags.Projects, id: `${districtId}-sync-${source}` },
      ],
      transformResponse: (
        result: IApiResponse<IResolveHousingCoverageResponse>,
      ) => result.data,
    }),
    syncNewHousingIngest: builder.mutation<
      { accepted: true; districtId: string; source: string },
      ISyncNewHousingIngestRequest
    >({
      query: (body) => ({
        url: "/projects/ingest/sync",
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { districtId, source }) => [
        { type: ApiTags.Projects, id: `${districtId}-sync-${source}` },
        { type: ApiTags.Projects, id: `${districtId}-review-counts` },
        { type: ApiTags.Projects, id: "LIST" },
      ],
      transformResponse: (
        result: IApiResponse<{
          accepted: true;
          districtId: string;
          source: string;
        }>,
      ) => result.data,
    }),
    rescanNewHousingIngest: builder.mutation<
      { accepted: true; districtId: string; source: string },
      ISyncNewHousingIngestRequest
    >({
      query: (body) => ({
        url: "/projects/ingest/rescan",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { districtId, source }) => [
        { type: ApiTags.Projects, id: `${districtId}-sync-${source}` },
        { type: ApiTags.Projects, id: `${districtId}-review-counts` },
        { type: ApiTags.Projects, id: "LIST" },
      ],
      transformResponse: (
        result: IApiResponse<{ accepted: true; districtId: string; source: string }>,
      ) => result.data,
    }),
    publishNewHousingProject: builder.mutation<
      IProjectWithDetailsResponse,
      { projectId: string; districtId: string }
    >({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/publish`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, { projectId, districtId }) => [
        { type: ApiTags.Projects, id: projectId },
        { type: ApiTags.Projects, id: "LIST" },
        { type: ApiTags.Projects, id: `${districtId}-review-counts` },
      ],
      transformResponse: (result: IApiResponse<IProjectWithDetailsResponse>) =>
        result.data,
    }),
    unpublishNewHousingProject: builder.mutation<
      IProjectWithDetailsResponse,
      { projectId: string; districtId: string }
    >({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/unpublish`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, { projectId, districtId }) => [
        { type: ApiTags.Projects, id: projectId },
        { type: ApiTags.Projects, id: "LIST" },
        { type: ApiTags.Projects, id: `${districtId}-review-counts` },
      ],
      transformResponse: (result: IApiResponse<IProjectWithDetailsResponse>) =>
        result.data,
    }),
    rejectNewHousingProject: builder.mutation<
      IProjectWithDetailsResponse,
      { projectId: string; districtId: string }
    >({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/reject`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, { projectId, districtId }) => [
        { type: ApiTags.Projects, id: projectId },
        { type: ApiTags.Projects, id: "LIST" },
        { type: ApiTags.Projects, id: `${districtId}-review-counts` },
      ],
      transformResponse: (result: IApiResponse<IProjectWithDetailsResponse>) =>
        result.data,
    }),
    restoreNewHousingProject: builder.mutation<
      IProjectWithDetailsResponse,
      { projectId: string; districtId: string }
    >({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/restore`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, { projectId, districtId }) => [
        { type: ApiTags.Projects, id: projectId },
        { type: ApiTags.Projects, id: "LIST" },
        { type: ApiTags.Projects, id: `${districtId}-review-counts` },
      ],
      transformResponse: (result: IApiResponse<IProjectWithDetailsResponse>) =>
        result.data,
    }),
  }),
});

function syncProjectCachesFromDetails(
  dispatch: (action: unknown) => unknown,
  getState: () => unknown,
  projectId: string,
  details: IProjectWithDetailsResponse,
): void {
  if (details.id !== projectId) {
    return;
  }

  dispatch(
    projectsApi.util.updateQueryData(
      "getProjectWithDetails",
      projectId,
      () => details,
    ),
  );

  const state = getState() as Parameters<
    typeof projectsApi.util.selectCachedArgsForQuery
  >[0];

  for (const args of projectsApi.util.selectCachedArgsForQuery(
    state,
    "listNewHousingProjects",
  )) {
    dispatch(
      projectsApi.util.updateQueryData(
        "listNewHousingProjects",
        args,
        (draft) => {
          const item = draft.items.find((entry) => entry.id === projectId);
          if (!item) return;
          Object.assign(item, applyProjectDetailsToListItem(details));
        },
      ),
    );
  }
}

export const {
  useListNewHousingProjectsQuery,
  useCreateNewHousingProjectMutation,
  useUpdateNewHousingProjectMutation,
  useDeleteNewHousingProjectMutation,
  useReplaceProjectOccupancyScheduleMutation,
  useGetProjectGenerationRatesQuery,
  useLazyGetProjectGenerationRatesQuery,
  useUpdateProjectGenerationRatesMutation,
  useGetDistrictDefaultGenerationRatesQuery,
  useLazyGetDistrictDefaultGenerationRatesQuery,
  useUpsertDistrictDefaultGenerationRatesMutation,
  useCreateProjectUploadUrlMutation,
  useImportNewHousingMutation,
  useImportProjectBoundariesMutation,
  useCreateNewHousingGeocodingJobMutation,
  useCreateNewHousingReverseGeocodingJobMutation,
  useCreateProjectStudentsJobMutation,
  useFinalizeNewHousingDataImportMutation,
  useDeleteNewHousingDataImportMutation,
  useGetProjectWithDetailsQuery,
  useLazyGetProjectWithDetailsQuery,
  useGetProjectSchoolAllocationsQuery,
  useLazyGetProjectSchoolAllocationsQuery,
  useGetDistrictAffectedSchoolsDataQuery,
  useReplaceProjectSchoolAllocationsMutation,
  useGetNewHousingReviewCountsQuery,
  useGetHousingSourceSyncStatusQuery,
  useResolveHousingCoverageMutation,
  useSyncNewHousingIngestMutation,
  useRescanNewHousingIngestMutation,
  useUpdateHousingAutoSyncMutation,
  usePublishNewHousingProjectMutation,
  useUnpublishNewHousingProjectMutation,
  useRejectNewHousingProjectMutation,
  useRestoreNewHousingProjectMutation,
} = projectsApi;
export default projectsApi;
